"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { IdentificationCard } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { notFound, useParams } from "next/navigation";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DoubleCardAuth from "@/app/[lng]/auth/_components/double-card-auth/double-card-auth";
import LeftCardControlledLoginContent from "@/app/[lng]/auth/login/controlled/_components/shared/left-card-controlled-login-content/left-card-controlled-login-content";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { COUNTRIES, COUNTRIES_CODES } from "@/constants/countries";
import { PRIVACY_POLICIES } from "@/constants/links";
import evEnums from "@/enums";
import useControlledBasicProcessLogin from "@/hooks/use-controlled-basic-process-login";
import useEvbasicRealm from "@/hooks/use-evbasic-realm";
import { useToast } from "@/hooks/use-toast";
import useUpdateAuthUser from "@/hooks/use-update-auth-user";
import { useTranslation } from "@/i18n/client";
import topRightManWithComputer from "@/public/images/process/controlled/top-right-man-with-computer.svg";
import type { ApiResponse } from "@/repositories/common-types";
import ProcessRepository from "@/repositories/process/process-repository";
import type { ProcessPublic } from "@/repositories/process/types-core";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import type { CountryCode } from "@/types/countries";
import { handleApiError as parseApiError } from "@/utils/shared";

const DEFAULT_PHONE_COUNTRY_CODE = "CO";
const ID_AUTO_REGISTER_TYPE = "ID";
const PRIVACY_POLICIES_FULL_LINK = "https://www.evaluar.com/politica-de-privacidad/";
const PRIVACY_POLICIES_TERM_NAME = "PRIVACY_POLICIES";
const TERMS_AND_CONDITIONS_VERSION = 1;

export default function ProcessControlledAutoRegister() {
  const params = useParams();
  const processId = params.processId;

  const processRepository = new ProcessRepository();
  const { t: tAuto } = useTranslation("[processId]");
  const { t: tGlobal } = useTranslation("global");
  const { toast } = useToast();
  const { updateTokens } = useUpdateAuthUser();
  const setIntentUrl = usePersistedBoundStore.use.setIntentUrl();
  const setEnvironment = usePersistedBoundStore.use.setEnvironment();
  const setEnvironmentUrl = usePersistedBoundStore.use.setEnvironmentUrl();
  const { controlledBasicProcessLoginLoading } = useControlledBasicProcessLogin();

  useEvbasicRealm();

  useEffect(() => {
    if (!processId) {
      return;
    }
    setEnvironment({
      id: processId as string,
      realm: {
        Label: evEnums.SECURITY.RealmEnum.EVBASIC.Label,
        Name: evEnums.SECURITY.RealmEnum.EVBASIC.Label as "evbasic" | "evcore",
        RealmName: evEnums.SECURITY.RealmEnum.EVBASIC.RealmName as "evbasic" | "evcore",
      },
      fullProfile: false,
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      environmentType: {
        Label: evEnums.PROCESS.EnvironmentType.CONTROLLED.Label,
        Name: evEnums.PROCESS.EnvironmentType.CONTROLLED.Name as "CONTROLLED" | "EMAIL",
      },
    });
    setEnvironmentUrl(String(processId));
  }, [processId, setEnvironment, setEnvironmentUrl]);

  const { data, isLoading, isError } = useQuery<ApiResponse<ProcessPublic>>({
    queryKey: ["process-public", processId],
    queryFn: () => processRepository.getProcessPublic(processId as string),
    enabled: !!processId,
    retry: false,
  });
  const processData = data?.data;
  const shouldUseIdentification = processData?.autoRegisterType === ID_AUTO_REGISTER_TYPE;

  const lettersRegex = /^[A-Za-zÀ-ÿ\u00f1\u00d1\s]+$/;
  const phoneRegex = /^\d+$/;
  const emailSchema = shouldUseIdentification
    ? z.string().optional()
    : z.email({ error: tAuto("errors.emailInvalid") });
  const identificationSchema = shouldUseIdentification
    ? z.string().min(1, { error: tAuto("errors.identificationRequired") })
    : z.string().optional();
  const formSchema = z.object({
    firstName: z
      .string()
      .min(1, { error: tAuto("errors.firstNameRequired") })
      .min(3, { error: tAuto("errors.firstNameMin") })
      .regex(lettersRegex, { error: tAuto("errors.firstNameLetters") }),
    lastName: z
      .string()
      .min(1, { error: tAuto("errors.lastNameRequired") })
      .min(3, { error: tAuto("errors.lastNameMin") })
      .regex(lettersRegex, { error: tAuto("errors.lastNameLetters") }),
    email: emailSchema,
    identification: identificationSchema,
    phoneCountryCode: z.string().min(1, { error: tAuto("errors.phoneCountryCodeRequired") }),
    phoneNumber: z
      .string()
      .min(1, { error: tAuto("errors.phoneNumberRequired") })
      .regex(phoneRegex, { error: tAuto("errors.phoneNumberInvalid") }),
    country: z.string().min(1, { error: tAuto("errors.countryRequired") }),
    policies: z.boolean().refine((value) => value, {
      error: tAuto("errors.policiesRequired"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      identification: "",
      phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
      phoneNumber: "",
      country: "",
      policies: false,
    },
    mode: "onChange",
    shouldUseNativeValidation: false,
  });
  const emailValue = form.watch("email");
  const identificationValue = form.watch("identification");
  const hasRequiredIdentifier = shouldUseIdentification
    ? Boolean(identificationValue)
    : Boolean(emailValue);

  useEffect(() => {
    if (shouldUseIdentification) {
      form.clearErrors("email");
      return;
    }

    form.clearErrors("identification");
  }, [form, shouldUseIdentification]);

  const registerMutation = useMutation({
    mutationFn: (payload: z.infer<typeof formSchema>) => {
      const identification = shouldUseIdentification ? payload.identification : payload.email;
      const countryCode = payload.phoneCountryCode as CountryCode;
      const phoneNumber = `${COUNTRIES[countryCode].dialCode}${payload.phoneNumber}`;
      const termsDate = new Date().toISOString();
      const termsAndConditions = [
        {
          name: PRIVACY_POLICIES_TERM_NAME,
          date: termsDate,
          version: TERMS_AND_CONDITIONS_VERSION,
          link: PRIVACY_POLICIES,
        },
      ];

      return processRepository.postPublicControlledRegister(processId as string, {
        identification: identification ?? "",
        firstName: payload.firstName,
        lastName: payload.lastName,
        country: payload.country,
        autoRegisterType: processData?.autoRegisterType ?? "",
        phoneNumber: phoneNumber,
        termsAndConditions: termsAndConditions,
      });
    },
    onError: async (error) => {
      const apiError = parseApiError(error, {
        responseError: tGlobal("errors.api.default.responseError"),
        requestError: tGlobal("errors.api.default.requestError"),
        unknownError: tGlobal("errors.api.default.unknownError"),
      });

      toast({
        description: apiError.message,
        variant: "destructive",
      });
    },
    onSuccess: async (response) => {
      const token = response?.data?.token;
      if (token?.access_token) {
        setEnvironment((prev) => {
          const controlled = evEnums.PROCESS.EnvironmentType.CONTROLLED.Name;
          if (prev) {
            return {
              ...prev,
              environmentType: { ...prev.environmentType, Name: controlled },
            };
          }
          return {
            id: processId as string,
            realm: {
              Label: evEnums.SECURITY.RealmEnum.EVBASIC.Label,
              Name: evEnums.SECURITY.RealmEnum.EVBASIC.Label as "evbasic" | "evcore",
              RealmName: evEnums.SECURITY.RealmEnum.EVBASIC.RealmName as "evbasic" | "evcore",
            },
            fullProfile: false,
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            environmentType: {
              Label: evEnums.PROCESS.EnvironmentType.CONTROLLED.Label,
              Name: controlled as "CONTROLLED" | "EMAIL",
            },
          };
        });
        setIntentUrl(`/process/${processId}` as string);
        await updateTokens({
          access_token: token.access_token,
          refresh_token: token.refresh_token,
          token_type: token.token_type,
          expires_in: token.expires_in,
          preserveProfile: false,
        });
      }
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    registerMutation.mutate(values);
  }

  if (isError) {
    notFound();
  }
  const isSkeleton = isLoading || !processData || controlledBasicProcessLoginLoading;
  if (!isLoading && !processData && !controlledBasicProcessLoginLoading) {
    notFound();
  }

  return (
    <div className="min-h-full flex items-center justify-center">
      <DoubleCardAuth
        leftElement={
          <LeftCardControlledLoginContent>
            <section className="flex flex-col gap-5 mt-6">
              <div className="flex items-center gap-2">
                {isSkeleton ? (
                  <>
                    <Skeleton className="h-11 w-11 rounded-[8px]" />
                    <div className="flex flex-col gap-1">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </>
                ) : (
                  <>
                    {processData.companyLogoUrl && (
                      <Image
                        src={processData.companyLogoUrl}
                        alt="logo"
                        width={44}
                        height={44}
                        className="rounded-[8px]"
                      />
                    )}
                    <div className="flex flex-col gap-1">
                      <Typography variant="label 1" className="text-primary-800">
                        {processData.name}
                      </Typography>
                      <Typography variant="label 5" className="text-primary-800">
                        {processData.companyPublicName}
                      </Typography>
                    </div>
                  </>
                )}
              </div>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
                  <fieldset className="flex flex-col gap-3">
                    {isSkeleton ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Skeleton className="h-[19.2px] w-28" />
                            <Skeleton className="h-10 w-full rounded-md" />
                          </div>
                          <div className="space-y-1">
                            <Skeleton className="h-[19.2px] w-28" />
                            <Skeleton className="h-10 w-full rounded-md" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Skeleton className="h-[19.2px] w-28" />
                          <Skeleton className="h-10 w-full rounded-md" />
                        </div>
                        <div className="space-y-1">
                          <Skeleton className="h-[19.2px] w-72" />
                          <Skeleton className="h-10 w-full rounded-md" />
                        </div>
                        <div className="space-y-1">
                          <Skeleton className="h-[19.2px] w-28" />
                          <div className="grid grid-cols-[135px_1fr] gap-3">
                            <Skeleton className="h-10 w-full rounded-md" />
                            <Skeleton className="h-10 w-full rounded-md" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Skeleton className="h-[19.2px] w-28" />
                          <Skeleton className="h-10 w-full rounded-md" />
                        </div>
                        <div className="space-y-3 pt-1">
                          <Skeleton className="h-5 w-64 mx-auto" />
                          <div className="flex items-start gap-3">
                            <Skeleton className="h-5 w-5 rounded-md" />
                            <Skeleton className="h-10 w-full rounded-md" />
                          </div>
                        </div>
                        <div className="pt-2">
                          <Skeleton className="h-8 w-full rounded-md" />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{tAuto("firstNameLabel")}</FormLabel>
                                <FormControl>
                                  <Input placeholder={tAuto("firstNamePlaceholder")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{tAuto("lastNameLabel")}</FormLabel>
                                <FormControl>
                                  <Input placeholder={tAuto("lastNamePlaceholder")} {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <FormField
                          control={form.control}
                          name="country"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{tAuto("countryLabel")}</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder={tAuto("countryPlaceholder")} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {Object.entries(COUNTRIES).map(([code, info]) => (
                                    <SelectItem key={code} value={code}>
                                      {info.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {shouldUseIdentification ? (
                          <FormField
                            control={form.control}
                            name="identification"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>
                                  <span>{tAuto("identificationLabel")}</span>{" "}
                                  <span className="font-normal text-neutral-500">
                                    {tAuto("identificationHelp")}
                                  </span>
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    leftElement={
                                      <IdentificationCard size={20} className="text-neutral-300" />
                                    }
                                    placeholder={tAuto("identificationPlaceholder")}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : (
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{tAuto("emailLabel")}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="email"
                                    placeholder={tAuto("emailPlaceholder")}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}
                        <div className="space-y-1">
                          <FormLabel>{tAuto("phoneLabel")}</FormLabel>
                          <div className="grid grid-cols-[135px_1fr] gap-3">
                            <FormField
                              control={form.control}
                              name="phoneCountryCode"
                              render={({ field }) => {
                                const countryCode = field.value as CountryCode;
                                const country = COUNTRIES[countryCode];

                                return (
                                  <FormItem>
                                    <Select
                                      onValueChange={field.onChange}
                                      defaultValue={field.value}
                                    >
                                      <FormControl>
                                        <SelectTrigger
                                          leftElement={
                                            <Image
                                              alt={country.name}
                                              height={16}
                                              src={`/images/flags/${countryCode}.webp`}
                                              width={16}
                                            />
                                          }
                                        >
                                          <SelectValue
                                            placeholder={tAuto("phoneCountryCodePlaceholder")}
                                          >
                                            {country.dialCode}
                                          </SelectValue>
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {COUNTRIES_CODES.map(({ code, dialCode }) => {
                                          const itemCountryCode = code as CountryCode;
                                          const itemCountry = COUNTRIES[itemCountryCode];

                                          return (
                                            <SelectItem key={code} value={code}>
                                              {itemCountry.emoji} {itemCountry.name} {dialCode}
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                    <FormMessage />
                                  </FormItem>
                                );
                              }}
                            />
                            <FormField
                              control={form.control}
                              name="phoneNumber"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      inputMode="numeric"
                                      placeholder={tAuto("phoneNumberPlaceholder")}
                                      type="tel"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-3 pt-1">
                          <Typography variant="label 1" className="text-primary-600 text-center">
                            {tAuto("privacyPolicyTitle")}
                          </Typography>
                          <FormField
                            control={form.control}
                            name="policies"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-start gap-3">
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value}
                                      onCheckedChange={(checked) => {
                                        field.onChange(checked === true);
                                        form.trigger("policies");
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel
                                    className="text-neutral-600"
                                    typographyVariant="label 6"
                                  >
                                    {tAuto("privacyPolicyRequiredText")} {" "}
                                    <a
                                      className="text-primary-600 hover:text-primary-500"
                                      href={PRIVACY_POLICIES_FULL_LINK}
                                      rel="noreferrer"
                                      target="_blank"
                                    >
                                      política completa
                                    </a>
                                  </FormLabel>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="pt-2">
                          <Button
                            type="submit"
                            disabled={
                              !hasRequiredIdentifier ||
                              !form.formState.isValid ||
                              registerMutation.isPending
                            }
                            className="w-full h-10"
                            loading={registerMutation.isPending}
                          >
                            {tAuto("continue")}
                          </Button>
                        </div>
                        <div className="flex w-full flex-col items-center justify-center gap-6 pt-2">
                          <div className="flex w-full flex-row items-center gap-3">
                            <Separator orientation="horizontal" className="flex-1 bg-neutral-200" />
                            <Typography variant="caption 2" className="text-neutral-600">
                              O
                            </Typography>
                            <Separator orientation="horizontal" className="flex-1 bg-neutral-200" />
                          </div>

                          <Link href="/auth/login/controlled/basic">
                            <Typography
                              variant="label 1"
                              className="text-primary-600 text-center hover:text-primary-500"
                            >
                              {tAuto("loginLink")}
                            </Typography>
                          </Link>
                        </div>
                      </>
                    )}
                  </fieldset>
                </form>
              </Form>
            </section>
          </LeftCardControlledLoginContent>
        }
        topRightImage={{
          src: topRightManWithComputer,
          alt: "login",
        }}
      />
    </div>
  );
}
